import React, { useState, useEffect, useRef } from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/library/types';
import { AssignVehicleStyles } from './AssignVehicleModal.styles';
import config from 'src/globals';
import { GET, POST } from 'src/network/apiHandler';
import { GET_VEHICLES, GET_WORKERS } from 'src/api/routes';
import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Modal, Row, Form, Button, DatePicker, Col, message, Select, Input, Checkbox,
} from 'antd';
import { renderLabelAndInput } from 'src/library/common';
import { useGenericState } from 'src/library/UseGenericState';
import { assignVehicle, assignVehicleIST, fetchWorkerInfo, acceptIndentWithoutAssign, acceptIndentISTtWithoutAssign } from './AssignVehicle.api';
import moment from 'moment';
import lodash from 'lodash';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from 'src/utils/utils';
import { useDebounce } from 'src/library/hooks/useDebounce';

interface IProps extends StylesProps<ReturnType<typeof AssignVehicleStyles>> {
  isVisible: boolean;
  handleClose: (onSuccess?: boolean) => void;
  vehicleData;
  vendorConfig;
  phoneRegex;
  tripType;
  allowCrossVendorAssignment?;
}

interface StateProps {
  isLoading?: boolean;
}

const AssignVehicleModal = (props: IProps) : React.ReactElement => {
  const {
    classes,
    isVisible,
    handleClose,
    vehicleData,
    vendorConfig,
    phoneRegex,
    tripType,
    allowCrossVendorAssignment,
  } = props;

  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [acceptNowAssignLater, setAcceptNowAssignLater] = useState(false);
  const indentSettings = lodash.get(vendorConfig, 'indent_settings', {}) || {};
  const {
    enable_indent_acceptance: enableIndentAcceptance = false,
    allow_vendor_to_reschedule_assigned_trips: allowVendorRescheduleAssignedTrip = false,
  } = indentSettings;

  const handleCheckboxChange = (e) => {
    setAcceptNowAssignLater(e.target.checked);
  };

  const {
    id: tripId,
    hub_id: hubId,
    vehicle_registration_number: vehicleRegistrationNumber,
    worker_name: workerName,
    worker_code: workerCode,
    worker_contact_number: workerContactNumber,
    worker_license_number: workerLicenseNumber,
    worker_license_expiry_date: workerLicenseExpiryDate,
    vehicle_make_id: vehicleMakeId,
    custom_fields: customFields,
    vendor_trip_status,
    assigned_vendor_time,
    vendor_id,
    indent_assignment_deadline,
  } = vehicleData || {};

  const { API_BASE_URL } = config;
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isAddDriver, setIsAddDriver] = useState(false);
  const [isAddVehicle, setIsAddVehicle] = useState(false);
  const [state, setState] = useGenericState<StateProps>({ });
  const [vehicleSearchString, setVehicleSearchString] = useState('');
  const [driverSearchString, setDriverSearchString] = useState('');
  const [workersByCode, setWorkersByCode] = useState([]);
  const [workerCodeSearchString, setWorkerCodeSearchString] = useState('');
  const [selectedWorkerCode, setSelectedWorkerCode] = useState(workerCode || undefined);
  const [workerSelectionSource, setWorkerSelectionSource] = useState(null);
  const debouncedVehicleSearch = useDebounce(vehicleSearchString, 500);
  const debouncedDriverSearch = useDebounce(driverSearchString, 500);
  const debouncedWorkerCodeSearch = useDebounce(workerCodeSearchString, 500);
  const tripInputCustomFields = vendorConfig?.trips_properties_to_input || [];
  const vehicleInputRef = useRef(null);
  const driverNameInputRef = useRef(null);

  const {
    isLoading,
  } = state;

  const labelInputProps = {
    colSpan: {
      left: 7,
      right: 17,
    },
    form,
    classes,
    formStyles: { marginBottom: 15 },
  };

  const buildParams = (baseParams: any) => {
    const params: any = { ...baseParams };
    if (tripType === 'ist') {
      params.ist_id = tripId;
    } else {
      params.trip_id = tripId;
    }
    return params;
  };

  const fetchDrivers = async (queryStr?: string) => {
    const params = buildParams({ hubId, queryStr: queryStr || undefined });
    const response = await POST(API_BASE_URL + GET_WORKERS, params);
    if (response.isSuccess) {
      setDrivers(response.data);
    } else {
      message.error(t('Failed to fetch drivers'));
    }
  };

  const fetchVehicles = async (queryStr?: string) => {
    const params = buildParams({ vehicleMakeId, hubId, queryStr: queryStr || undefined });
    const response = await POST(API_BASE_URL + GET_VEHICLES, params);
    if (response.isSuccess) {
      setVehicles(response.data);
    } else {
      message.error(t('Failed to fetch vehicles'));
    }
  };

  const fetchWorkersByCode = async (searchCode: string) => {
    const params = buildParams({ hubId, worker_code: searchCode });
    const response = await POST(API_BASE_URL + GET_WORKERS, params);
    if (response.isSuccess) {
      setWorkersByCode(response.data);
    } else {
      message.error(t('Failed to fetch drivers'));
    }
  };

  useEffect(() => {
    if (isVisible && !isAddDriver) {
      fetchDrivers(debouncedDriverSearch);
    }
  }, [debouncedDriverSearch, isAddDriver]);

  useEffect(() => {
    if (isVisible && !isAddVehicle) {
      fetchVehicles(debouncedVehicleSearch);
    }
  }, [debouncedVehicleSearch, isAddVehicle]);

  useEffect(() => {
    if (isVisible && allowCrossVendorAssignment && !isAddDriver) {
      if (debouncedWorkerCodeSearch) {
        fetchWorkersByCode(debouncedWorkerCodeSearch);
      } else {
        setWorkersByCode([]);
      }
    }
  }, [debouncedWorkerCodeSearch, isAddDriver]);

  useEffect(() => {
    if (isAddVehicle && vehicleInputRef?.current) {
      vehicleInputRef.current.focus();
    }
  }, [isAddVehicle]);

  useEffect(() => {
    if (isAddDriver && driverNameInputRef.current) {
      driverNameInputRef.current.focus();
    }
  }, [isAddDriver]);

  const onDriverSelectChange = (value) => {
    const selectedDriver = drivers.find((driver) => driver.worker_code === value?.key);
    setSelectedWorkerCode(selectedDriver.worker_code);
    setWorkerSelectionSource('name');
    form.setFieldsValue({
      workerCode: { key: selectedDriver.worker_code, label: selectedDriver.name },
      workerName: selectedDriver.name,
      workerLicenseNumber: selectedDriver.driving_licence_number,
      workerContactNumber: selectedDriver.phone,
      workerLicenseExpiryDate: selectedDriver.driving_licence_expiry_date
        ? moment(selectedDriver.driving_licence_expiry_date)
        : null,
    });
  };

  const onWorkerCodeSelectChange = (value) => {
    const selectedDriver = workersByCode.find((driver) => driver.worker_code === value);
    if (!selectedDriver) {
      return;
    }
    setSelectedWorkerCode(selectedDriver.worker_code);
    setWorkerSelectionSource('code');
    form.setFieldsValue({
      workerCode: { key: selectedDriver.worker_code, label: selectedDriver.name },
      workerName: selectedDriver.name,
      workerLicenseNumber: selectedDriver.driving_licence_number,
      workerContactNumber: selectedDriver.phone,
      workerLicenseExpiryDate: selectedDriver.driving_licence_expiry_date
        ? moment(selectedDriver.driving_licence_expiry_date)
        : null,
    });
  };

  const addDriver = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsAddDriver(true);
    setDriverSearchString('');
    setSelectedWorkerCode(undefined);
    setWorkerSelectionSource(null);
    form.setFieldsValue({
      workerCode: { key: '', label: '' },
      workerLicenseNumber: '',
      workerContactNumber: '',
      workerLicenseExpiryDate: undefined,
      workerName: '',
    });
  };

  const onVehicleInputChange = (e) => {
    form.setFieldsValue({
      vehicleRegistrationNumber: e.target.value,
    });
    if (!e.target.value) {
      setIsAddVehicle(false);
    }
  };

  const onDriverInputChange = (e) => {
    form.setFieldsValue({
      workerName: e.target.value,
    });
    if (!e.target.value) {
      setIsAddDriver(false);
    }
  };

  const addVehicle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsAddVehicle(true);
    setVehicleSearchString('');
    form.setFieldsValue({
      vehicleRegistrationNumber: '',
    });
  };

  const renderVehicleRegNumber = () : React.ReactElement => {
    return (
      <>
        <Row>
          <Col span={labelInputProps.colSpan?.left} className={classes.label}>
            {t('Vehicle Reg Number')} <span style={{ color: '#E83939', fontSize: '14px' }}>*</span>
          </Col>
          <Col span={labelInputProps.colSpan?.right}>
            <Form form={form}>
              <Form.Item
                name="vehicleRegistrationNumber"
                style={labelInputProps.formStyles}
                rules={[
                  { required: !enableIndentAcceptance, message: t('Required') },
                  {
                    validator: (_, value) => {
                      return !value.includes(" ")
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("No spaces allowed")));
                    },
                  },
                ]}
                initialValue={vehicleRegistrationNumber}
              >
                {isAddVehicle ? (
                  <Input
                    ref={vehicleInputRef}
                    placeholder={t("Enter")}
                    className={classes.inputField}
                    onChange={onVehicleInputChange}
                  />
                ) : (
                  <Select
                    placeholder={t("Enter")}
                    showSearch
                    onSearch={(value) => setVehicleSearchString(value)}
                    filterOption={false}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {vendorConfig?.vehicle_and_driver?.allow_new_vehicle_creation && (
                          <Button type="text" icon={<PlusOutlined />} onClick={addVehicle}>
                            {t('Add item')}
                          </Button>
                        )}
                      </>
                    )}
                    options={vehicles.map((vehicles) => ({
                      key: vehicles.id,
                      label: vehicles.vehicle_number,
                      value: vehicles.vehicle_number,
                    }))}
                  />
                )}
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </>
    );
  };

  const renderWorkerCodeSearch = () : React.ReactElement => {
    return (
      <Row style={{ marginBottom: 15 }}>
        <Col span={labelInputProps.colSpan?.left} className={classes.label}>
          {t('Worker Code')}
        </Col>
        <Col span={labelInputProps.colSpan?.right}>
          <Select
            placeholder={t("Enter")}
            showSearch
            value={selectedWorkerCode}
            disabled={workerSelectionSource === 'name'}
            onSearch={(value) => setWorkerCodeSearchString(value)}
            onChange={onWorkerCodeSelectChange}
            filterOption={false}
            style={{ width: '100%' }}
            options={workersByCode.map((driver) => ({
              key: driver.id,
              label: driver.worker_code,
              value: driver.worker_code,
            }))}
          />
        </Col>
      </Row>
    );
  };

  const renderDriverName = () : React.ReactElement => {
    return (
      <>
        <Row>
          <Col span={labelInputProps.colSpan?.left} className={classes.label}>
            {t('Driver Name')} <span style={{ color: '#E83939', fontSize: '14px' }}>*</span>
          </Col>
          <Col span={labelInputProps.colSpan?.right}>
            <Form form={form}>
              {isAddDriver ? (
                <Form.Item
                  name="workerName"
                  style={labelInputProps.formStyles}
                  rules={[
                    { required: !enableIndentAcceptance, message: t('Required') },
                  ]}
                  initialValue={workerName}
                >
                  <Input
                    ref={driverNameInputRef}
                    placeholder={t("Enter")}
                    className={classes.inputField}
                    onChange={onDriverInputChange}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="workerCode"
                  style={labelInputProps.formStyles}
                  rules={[
                    { required: !enableIndentAcceptance, message: t('Required') },
                  ]}
                  initialValue={{ key: workerCode, label: workerName }}
                >
                  <Select
                    placeholder={t("Enter")}
                    onChange={onDriverSelectChange}
                    showSearch
                    disabled={workerSelectionSource === 'code'}
                    onSearch={(value) => setDriverSearchString(value)}
                    filterOption={false}
                    labelInValue
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        {vendorConfig?.vehicle_and_driver?.allow_new_driver_creation && (
                          <Button type="text" icon={<PlusOutlined />} onClick={addDriver}>
                            {t('Add item')}
                          </Button>
                        )}
                      </>
                    )}
                    options={drivers.map((driver) => ({
                      key: driver.id,
                      label: driver.name,
                      value: driver.worker_code,
                    }))}
                  />
                </Form.Item>
              )}
            </Form>
          </Col>
        </Row>
      </>
    );
  };

  const handleDriverSearch = async () => {
    const workerContactNumber = form.getFieldValue('workerContactNumber');

    if (workerContactNumber.length !== 10) {
      return;
    }

    const body = {
      workerCode: workerContactNumber,
    };

    const response = await fetchWorkerInfo(body);
    if (response.isSuccess) {
      const { data } = response;
      if (Array.isArray(data) && data.length) {
        const workerName = lodash.get(data, '[0].worker_name') || '';
        form.setFieldsValue({ workerName });
      }
    } else {
      message.error(response.errorMessage);
    }
  };

  const renderDriverMobile = () : React.ReactElement => {
    return (
      <Row>
        {renderLabelAndInput({
          ...labelInputProps,
          label: t('Driver Mobile'),
          key: 'workerContactNumber',
          initialValue: workerContactNumber,
          placeholder: t('Enter'),
          rules: [{
            required: !enableIndentAcceptance, message: t('Required'),
          },
          ...(phoneRegex ? [{ pattern: phoneRegex, message: t('Please Enter valid phone number') }] : []),
          ],
          type: 'text',
          disabled: !isAddDriver,
          isRequired: true,
        })}
      </Row>
    );
  };

  const renderDriverLicenseNumber = () : React.ReactElement => {
    return (
      <Row>
        {renderLabelAndInput({
          ...labelInputProps,
          label: t('Driver License Number'),
          key: 'workerLicenseNumber',
          initialValue: workerLicenseNumber,
          type: 'text',
          placeholder: t('Enter'),
          labelStyles: { marginTop: 0 },
          disabled: !isAddDriver,
        })}
      </Row>
    );
  };

  const renderLicenseExpiry = () : React.ReactElement => {
    return (
      <Row>
        <Col span={7} className={classes.label}>
          {t('License Expiry')}
        </Col>
        <Col span={17}>
          <Form form={form}>
            <Form.Item
              style={{ marginBottom: '15px' }}
              name="workerLicenseExpiryDate"
              initialValue={workerLicenseExpiryDate ? moment(workerLicenseExpiryDate) : undefined}
            >
              <DatePicker
                placeholder={t("Select")}
                style={{ width: '184px' }}
                format="DD/MM/YYYY"
                disabled={!isAddDriver}
              />
            </Form.Item>
          </Form>
        </Col>
      </Row>
    );
  };

  const renderCustomFields = (): React.ReactElement[] => {
    return tripInputCustomFields.map((field) => {
      if (field.key) {
        return (
          <Row key={field.key}>
            {renderLabelAndInput({
              ...labelInputProps,
              label: t(field.label),
              key: ['customFields', field.key],
              type: field.type,
              initialValue: customFields?.[field.key],
              placeholder: t('Enter'),
              labelStyles: { marginTop: 0 },
              rules: field.isRequired ? [{ required: true, message: t('Required') }] : [],
              isRequired: field.isRequired,
              formUIComponent: field.formUIComponent,
            })}
          </Row>
        );
      }
      return null;
    });
  };

  const showReassignmentConfirmModal = async (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: t('Confirm Re-assignment'),
        icon: <WarningOutlined style={{ color: '#faad14' }} />,
        content: (
          <p>
            {t(
              'Are you sure you want to re-assign the vehicle/worker to a new assignment?',
            )}
          </p>
        ),
        okText: t('Yes'),
        cancelText: t('Cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const handleOk = async () : Promise<void> => {
    try {
      const formData = await form.validateFields();

      const hasPartialAssignment =
        (vehicleRegistrationNumber &&
          formData.vehicleRegistrationNumber !== vehicleRegistrationNumber) ||
        (workerCode && formData.workerCode?.key !== workerCode);

      if (hasPartialAssignment && allowVendorRescheduleAssignedTrip) {
        const confirmed = await showReassignmentConfirmModal();
        if (!confirmed) {
          return;
        }
      }
      setState({ isLoading: true });
      const body = {
        ...formData,
        workerCode: formData.workerCode?.key,
        workerName: formData?.workerName || formData.workerCode?.label,
        tripId,
        is_scheduled: true,
        vendor_id,
        skipTripAssignmentChecks: allowVendorRescheduleAssignedTrip,
        workerLicenseExpiryDate: formData.workerLicenseExpiryDate ?
          moment(formData.workerLicenseExpiryDate).format('YYYY-MM-DD') :
          undefined,
      };

      tripInputCustomFields?.forEach((entry) => {
        const fieldValue = lodash.get(formData, ['customFields', entry?.key]);
        if (entry?.type === 'datetime' && fieldValue) {
          lodash.set(body, ['customFields', entry?.key], new Date(fieldValue).getTime());
        } else {
          const value = (fieldValue === '' || fieldValue === undefined) ? null : fieldValue;
          lodash.set(body, ['customFields', entry?.key], value);
        }
      });

      if (tripType === 'ist') {
        body['istId'] = tripId;
      }
      const response = tripType === 'ist' ? await assignVehicleIST(body) : await assignVehicle(body);
      if (response.isSuccess) {
        handleClose(true);
      } else {
        message.error(response.errorMessage);
      }
      setState({ isLoading: false });
    } catch (err) {
      setState({ isLoading: false });
    }
  };

  const renderAssignLaterCheckbox = (): React.ReactElement => {
    return (
      <Row style={{ marginBottom: '20px' }}>
        <Col span={24}>
          <Form form={form}>
            <Form.Item
              name="acceptNowAssignLater"
              valuePropName="checked"
              initialValue={acceptNowAssignLater}
              style={{ marginBottom: 0 }}
            >
              <Checkbox
                onChange={(e) => {
                  setAcceptNowAssignLater(e.target.checked);
                  form.setFieldsValue({ acceptNowAssignLater: e.target.checked });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{
                  marginLeft: '8px',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.85)',
                }}
                >
                  {t('Accept now, assign later')}
                </span>
              </Checkbox>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    );
  };

  const handleConfirmAcceptance = () => {
    Modal.confirm({
      title: t('Confirm Acceptance'),
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      content: (
        <>
          <p>{t('You are accepting this indent without assigning a driver and vehicle.')}</p>
          {indent_assignment_deadline && (
            <p>
              {t('You must complete the assignment before')}{' '}
              <b>
                {formatDateTime(indent_assignment_deadline)}
              </b>.
            </p>
          )}
        </>
      ),
      okText: t('I Understand'),
      cancelText: t('Cancel'),
      onOk: async () => {
          const response = tripType === 'ist' ? await acceptIndentISTtWithoutAssign({ istId: tripId, vendor_id }) : await acceptIndentWithoutAssign({ tripId, vendor_id });
          if (response.isSuccess) {
            message.success(t('Indent accepted successfully'));
            handleClose(true);
          } else {
            message.error(response.errorMessage);
          }
      },
    });
  };

  return (
    <Modal
      visible={isVisible}
      title={t("Assign Vehicle")}
      closable={false}
      onCancel={() => handleClose()}
      className={classes.modal}
      footer={(
        <div>
          <Button
            onClick={() => handleClose()}
            size="large"
            style={{ border: '1px solid rgba(66, 66, 66, 0.5)', backgroundColor: '#FFFFFF', color: '#424242' }}
            className={classes.button}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={
              enableIndentAcceptance && acceptNowAssignLater
                ? handleConfirmAcceptance
                : handleOk
            }
            size="large"
            style={{ border: '1px solid #0780FE', backgroundColor: '#0780FE', color: '#FFFFFF' }}
            className={classes.button}
            loading={isLoading}
          >
            {acceptNowAssignLater ? t('Confirm Acceptance') : t('Select')}
          </Button>
        </div>
      )}
    >
      {enableIndentAcceptance && vendor_trip_status === 'created' && renderAssignLaterCheckbox()}
      {!acceptNowAssignLater && (
        <>
          {renderVehicleRegNumber()}
          {renderDriverName()}
          {allowCrossVendorAssignment && !isAddDriver && renderWorkerCodeSearch()}
          {renderDriverMobile()}
          {renderDriverLicenseNumber()}
          {renderLicenseExpiry()}
          {renderCustomFields()}
        </>
      )}
    </Modal>
  );
};

export default withStyles(AssignVehicleStyles)(AssignVehicleModal);
